import React from 'react'
import { makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBBtnGroup,
  MDBCollapse,
} from 'mdb-react-ui-kit'
import { addWeeks, startOfWeek, format, getQuarter, getISOWeek } from 'date-fns'
import { UIDatePickerInput, UIInput } from './../../../../components/forms/form-fields'
import DateManager from '../../../../shared/utilities/DateManager.class'
import ProductionWidget from './ProductionWidget.component'
import ComparativeDateCalculator from './ComparativeDateCalculator.class'
import { CarrierFactory } from '../../../../shared/factories'

import './AgentWidget.scss'

const ENABLE_PER_DAY = false
let timerInt = null

class CarrierSelectorStore {
  searchTerm = ''
  selectedDownline = ''
  isOpen = false
  carriers = []

  constructor() {
    makeAutoObservable(this)
  }

  onSearchKeyDown = evt => {
    if (evt.key === 'Enter' && this.searchTerm.length > 2) this.performSearch()
  }

  performSearch = async () => {
    let carriers = []
    if (this.searchTerm) {
      carriers =
        (await CarrierFactory.search({
          search: { _global: this.searchTerm },
          pagination: false,
        })) || []
    }

    this.carriers = carriers.sort((a, b) => `${a.get('c_name')}`.localeCompare(`${b.get('c_name')}}`, 'en'))

    this.isOpen = this.carriers.length > 0

    return Promise.resolve(this.carriers)
  }
}

const Store = new CarrierSelectorStore()

const CarrierSelectorComponent = ({ onCarrierSelected }) => {
  return (
    <MDBRow id='GrowthWidgetCarrierSelectorComponent'>
      <MDBCol size='12' md='10' style={{ position: 'relative' }}>
        <UIInput
          type='text'
          label='Search Carriers'
          value={Store.searchTerm}
          onKeyDown={evt => Store.onSearchKeyDown(evt)}
          onChange={e => {
            const v = e.target.value
            Store.searchTerm = v

            if (timerInt) {
              window.clearTimeout(timerInt)
              timerInt = null
            }

            timerInt = setTimeout(() => {
              Store.performSearch()

              if (timerInt) {
                window.clearTimeout(timerInt)
                timerInt = null
              }
            }, 1000)
          }}
        />
      </MDBCol>
      <MDBCol size='12' md='2' style={{ position: 'relative', display: 'flex', justifyContent: 'end' }}>
        <MDBBtn
          type='button'
          disabled={Store.searchTerm === ''}
          onClick={() => {
            Store.searchTerm = ''
            Store.selectedDownline = null
            Store.carriers = []
            Store.isOpen = false
            onCarrierSelected(false)
          }}
          color='secondary'
          style={{ display: 'flex' }}
        >
          <span>
            <MDBIcon icon='times' />
          </span>
          &nbsp;<span>Reset</span>
        </MDBBtn>
      </MDBCol>
      <div className='dropdown-wrapper'>
        <div className='dropdown-inner'>
          <MDBCollapse open={Store.isOpen}>
            <ul>
              {Store.carriers.map((carrier, idx) => {
                return (
                  <li
                    key={`carrier-selectable--${carrier.id()}-${idx}`}
                    className='carrier-option'
                    onClick={() => {
                      Store.searchTerm = carrier.get('c_name').trim()
                      Store.isOpen = false
                      onCarrierSelected(carrier)
                    }}
                  >
                    <strong>{carrier.get('c_name')}</strong>
                    <br />
                    <small>{carrier.get('c_description')} </small>
                  </li>
                )
              })}
            </ul>
          </MDBCollapse>
        </div>
      </div>
    </MDBRow>
  )
}

const CarrierSelector = observer(CarrierSelectorComponent)

class CarrierGrowthReportWidget extends ProductionWidget {
  Calc = null

  state = {
    reportDate: new Date(),
    metric: 'points',
    data: {},
    prevYear: new Date().getFullYear() - 1,
    diffPrevYear: 1,
    fetching: true,
    error: null,
  }

  componentDidMount() {
    this._searchMethod = 'searchCarrier'
    this.Calc = new ComparativeDateCalculator()
    this._fetch()
  }

  render() {
    const { fetching } = this.state

    return (
      <MDBContainer className='ProductionWidgetComponent' id='CarrierWidgetComponent' fluid>
        <MDBRow>
          <MDBCol size='12'>
            <CarrierSelector
              onCarrierSelected={carrier => {
                if (carrier) {
                  this._searchId = carrier.id()
                  this._fetchByDownline()
                } else if (carrier === false) {
                  this._searchId = ''
                  this._fetch()
                }
              }}
            />
          </MDBCol>
          <MDBCol size='12'>
            <ul className='source-buttons'>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'points'}
                  onClick={() => {
                    this.setState({ metric: 'points' })
                  }}
                  color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Points
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'av'}
                  onClick={() => {
                    this.setState({ metric: 'av' })
                  }}
                  color={this.state.metric !== 'av' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View AV
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'aca_lives'}
                  onClick={() => {
                    this.setState({ metric: 'aca_lives' })
                  }}
                  color={this.state.metric !== 'aca_lives' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View ACA Lives
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'group'}
                  onClick={() => {
                    this.setState({ metric: 'group' })
                  }}
                  color={this.state.metric !== 'group' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Groups
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'ma_lives'}
                  onClick={() => {
                    this.setState({ metric: 'ma_lives' })
                  }}
                  color={this.state.metric !== 'ma_lives' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Med Advantange
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'life_av'}
                  onClick={() => {
                    this.setState({ metric: 'life_av' })
                  }}
                  color={this.state.metric !== 'life_av' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Life
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'med_supps_av'}
                  onClick={() => {
                    this.setState({ metric: 'med_supps_av' })
                  }}
                  color={this.state.metric !== 'med_supps_av' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Med Supps
                </MDBBtn>
              </li>
            </ul>
          </MDBCol>
        </MDBRow>
        <hr />
        <MDBRow>
          <MDBCol size='12'>
            <MDBContainer fluid>
              <MDBRow className='widget-date-selector'>
                <MDBCol size='12' sm='12' md='4' className='wds-input'>
                  <UIDatePickerInput
                    label='Report Date'
                    name='report_date'
                    id='dpw_report_date'
                    showYearDropdown
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                    dateFormat='MM/dd/yyyy'
                    maxDate={new Date()}
                    selected={this.state.reportDate}
                    onChange={date => this._onDateChange(date?.target?.value)}
                  />
                </MDBCol>
                <MDBCol size='12' sm='12' md='8' className='wds-preset'>
                  <MDBBtnGroup>
                    <MDBBtn
                      type='button'
                      disabled={fetching}
                      onClick={() => this._onDateChange(DateManager.getDayInPast('Sun', this.state.reportDate))}
                      color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                    >
                      <div>
                        <MDBIcon icon='arrow-left' />
                      </div>
                      &nbsp;<div>Previous Week</div>
                    </MDBBtn>
                    <MDBBtn
                      type='button'
                      disabled={fetching}
                      onClick={() => this._onDateChange(new Date())}
                      color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                    >
                      <div>Current Week</div>
                    </MDBBtn>
                    <MDBBtn
                      type='button'
                      disabled={fetching || !this._canAdvanceDate()}
                      onClick={() =>
                        this._onDateChange(DateManager.getDayInPast('Sun', addWeeks(this.state.reportDate, 2)))
                      }
                      color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                    >
                      <div>Next Week</div>
                      &nbsp;
                      <div>
                        <MDBIcon icon='arrow-right' />
                      </div>
                    </MDBBtn>
                  </MDBBtnGroup>
                </MDBCol>
              </MDBRow>
            </MDBContainer>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol size='12' sm='12' md='4'>
            <MDBCard className='current-year'>
              <MDBCardHeader color='default-color' className='default-color'>
                Current Year
              </MDBCardHeader>
              <MDBCardBody>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>
                      Week # {getISOWeek(startOfWeek(this.state.reportDate, { weekStartsOn: 1 }))}
                    </h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.wtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Month - {format(this.state.reportDate, 'LLLL')}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.mtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Quarter {getQuarter(this.state.reportDate)}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.qtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Year {format(this.state.reportDate, 'yyyy')}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.ytd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow className='card-footer'>
                  {ENABLE_PER_DAY ? (
                    <MDBCol size='12'>
                      <h6 className='float-left'>{format(this.state.reportDate, 'yyyy')} Per-Day</h6>
                      <h6 className='float-right'>
                        {!fetching
                          ? this._formatMetric(
                              this._forecastMetric(this.state.data?.current?.ytd[this.state.metric] || 0)?.dailyRate,
                              true
                            )
                          : '-'}
                      </h6>
                    </MDBCol>
                  ) : (
                    <></>
                  )}
                  <MDBCol size='12'>
                    <h5 className='float-left'>{format(this.state.reportDate, 'yyyy')} Forecast</h5>
                    <h5 className='float-right'>
                      {!fetching
                        ? this._formatMetric(
                            this._forecastMetric(this.state.data?.current?.ytd[this.state.metric] || 0)?.forecastedValue
                          )
                        : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol size='12' sm='12' md='4'>
            <MDBCard className='previous-year'>
              <MDBCardHeader color='primary-color' className='prev-year primary-color'>
                Previous Year{' '}
                <span className='ml-3'>
                  {this.state.fetching ? <i className='fa fa-spin fa-spinner'></i> : this._renderPrevYearDropDown()}
                </span>
              </MDBCardHeader>
              <MDBCardBody>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>
                      Week # {getISOWeek(startOfWeek(this.state.reportDate, { weekStartsOn: 1 }))}
                    </h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.wtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Month - {format(this.state.reportDate, 'LLLL')}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.mtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Quarter {getQuarter(this.state.reportDate)}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.qtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Year {this.state.prevYear}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.ytd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow className='card-footer'>
                  {ENABLE_PER_DAY ? (
                    <MDBCol size='12'>
                      <h6 className='float-left'>{this.state.prevYear} Per-Day</h6>
                      <h6 className='float-right'>
                        {!fetching
                          ? this._formatMetric(
                              this._forecastMetric(this.state.data?.previous?.ytd[this.state.metric] || 0, 'prev_year')
                                ?.dailyRate,
                              true
                            )
                          : '-'}
                      </h6>
                    </MDBCol>
                  ) : (
                    <></>
                  )}
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol size='12' sm='12' md='4'>
            <MDBCard className='year-growth'>
              <MDBCardHeader color='secondary-color' className='secondary-color'>
                % Difference
              </MDBCardHeader>
              <MDBCardBody>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.wtd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.mtd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.qtd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.ytd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow className='card-footer'>
                  {ENABLE_PER_DAY ? (
                    <MDBCol size='12'>
                      <h6 className='float-left'>Per-Day Difference</h6>
                      <h6 className='float-right'>
                        {!fetching
                          ? this._formatMetric(
                              this._forecastMetric(this.state.data?.current?.ytd[this.state.metric] || 0)?.dailyRate -
                                this._forecastMetric(
                                  this.state.data?.previous?.ytd[this.state.metric] || 0,
                                  'prev_year'
                                )?.dailyRate,
                              true
                            )
                          : '-'}
                      </h6>
                    </MDBCol>
                  ) : (
                    <></>
                  )}
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    )
  }
}

export default CarrierGrowthReportWidget
